/**
 * MockChatPage — dev-only harness at `/mock-chat`.
 *
 * Exercises `VirtualList`'s new `reversed` mode (Task 1) with a cached
 * `string[]` and a composer:
 *
 * - Renders messages in chronological order (oldest at index 0, newest at
 *   the end) — `VirtualList(reversed)` anchors the viewport to the **bottom**
 *   on first mount so the newest message is visible.
 * - Typing in the composer + pressing Enter (or clicking send) appends to
 *   the list. When the user is pinned to the bottom, the viewport sticks
 *   to the bottom as the list grows; when the user has scrolled up to read
 *   older messages, the viewport is left alone.
 * - Scrolling up far enough fires the top sentinel's `onReachedEnd`, which
 *   prepends a batch of synthetic "older" messages. `VirtualList(reversed)`
 *   preserves the viewport offset across the prepend so the user's reading
 *   position is not yanked.
 *
 * There is no Flutter counterpart for this page — `VirtualList` is a
 * web-only primitive and this is a manual test harness for it. Not exposed
 * in the TopBar; reachable only by typing `/mock-chat` in the URL or via
 * `Navigator.navigateToMockChat()` from the console.
 *
 * Structural template: `ContactPage` / `SupportPage` — same `useBasePage` +
 * screen-key wiring, same shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). Body diverges:
 * a single full-height scrollable region hosting `VirtualList` plus a pinned
 * `MessageInput` at the bottom.
 *
 * App-bar provenance: this page has no Flutter counterpart at all, so its bar
 * is web-only by construction; it adopts the shared component purely for
 * consistency with the other page bars (shape precedent:
 * `lib/presentation/custom_widgets/base_app_bar.dart:5-42`). No
 * Flutter-parity claim.
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { MessageInput, type MessageInputHandle } from '@/presentation/components/common/MessageInput';
import { VirtualList, type VirtualListHandle } from '@/presentation/components/common/virtualList';
import { useMockChat } from './hooks/useMockChat';
import type { MockedChatMessageEntity } from './types/mockedChatMessageEntity';

/**
 * `defaultItemHeight` hint for the chat bubbles. Plain-text rows in this
 * harness measure ~40–60px once laid out; passing a realistic value (vs
 * `VirtualList`'s built-in 600px default tuned for full-bleed content cards)
 * keeps the initial render window honest and prevents the top load-more
 * sentinel from sitting too close to the visible area on mount.
 */
const MOCK_CHAT_DEFAULT_ITEM_HEIGHT_PX = 48;

export const MockChatPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();

    // Canonical screen-key wiring (mirrors `ContactPage` / `SupportPage`).
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    const {
        messages,
        sendMessage,
        prependOlderMessages,
    } = useMockChat();

    // `VirtualList` listens to this element's scroll events. The page body
    // is laid out as a flex column: app bar + scroll region (flex:1) +
    // composer; the scroll region is the explicit overflow owner.
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Imperative handle for the inner `VirtualList` — used to drive an
    // explicit `scrollToBottom()` after the user sends a new message, so the
    // viewport deterministically follows the append regardless of whether the
    // user was previously pinned to the bottom or had scrolled up.
    const virtualListRef = useRef<VirtualListHandle>(null);

    // Imperative handle for the composer. `MessageInput` self-owns its draft
    // text (uncontrolled) so per-keystroke updates do not re-render this
    // page (and the inner `VirtualList`). We hold a ref to call `clear()`
    // after a successful `sendMessage` — see Finding 5 in
    // `workflow/user_reviews/feat_virtual_list_reversed_support_fix_plan_3.md`.
    const messageInputRef = useRef<MessageInputHandle>(null);

    // Wrap `sendMessage` so the caller-driven `scrollToBottom` runs on the
    // append path ONLY. An effect keyed on `messages` would also fire after
    // `prependOlderMessages`, which would yank the user from their reading
    // position when they hit the top sentinel — exactly the opposite of the
    // desired behavior. The `requestAnimationFrame` gives React time to
    // commit the new message to the DOM (so `scrollHeight` reflects it) and
    // lets the items-change effect inside `useReversedAnchor` run first; the
    // explicit `scrollToBottom` then OVERRIDES the hook's "leave alone"
    // decision in the user-scrolled-up case, which is the user's stated
    // intent when they send a message.
    const handleSend = useCallback((text: string) => {
        const sent = sendMessage(text);
        if (!sent) return;
        messageInputRef.current?.clear();
        requestAnimationFrame(() => {
            virtualListRef.current?.scrollToBottom();
        });
    }, [sendMessage]);

    // Deep-link guard: with no in-app history, fall back
    // to the feed (same pattern as ContactPage / SupportPage).
    const handleBack = () => Navigator.goBackOr(() => Navigator.navigateToFeed());

    // Stable per-message identity key. Each `MockedChatMessageEntity` carries
    // a `uniqueKey` minted in `useMockChat` from a monotonically-increasing
    // counter, with an origin prefix (`seed-`, `sent-`, `older-`). This is
    // what `VirtualList`'s `getItemKey` returns — independent of the rendered
    // `text`, so two messages with identical text (e.g. the user sending
    // "Ok" twice) remain distinct list entries instead of collapsing into a
    // single DOM node + height slot.
    //
    // This is also load-bearing for `VirtualList(reversed)`: the prepend-vs-
    // append discriminator compares `items[0]`'s key across renders. With
    // index keys (`(_, i) => i`), `getItemKey(items[0], 0)` is always `0`,
    // so prepends would be misclassified as appends and the preserve-offset
    // branch would never run. With `uniqueKey`, the first item's key changes
    // exactly when a new batch is prepended (each prepended entry carries a
    // fresh `uniqueKey`) and stays put across appends (existing entries keep
    // their keys), which is what the discriminator needs. It also keeps the
    // top sentinel from auto-firing during the first-mount bottom-anchor RAF
    // window: with a real prepend path, the first batch correctly compensates
    // `scrollTop` and pushes the sentinel off-screen instead of leaving it
    // intersecting at `scrollTop=0`.
    const getItemKey = useMemo(
        () => (msg: MockedChatMessageEntity) => msg.uniqueKey,
        [],
    );

    // Per-bubble breathing room for the first and last item. The scroll
    // region no longer carries `paddingTop`/`paddingBottom` (see the
    // `VirtualList(reversed)` JSDoc — vertical padding on the scroll region
    // would couple the bottom-pin math to this caller's computed style and
    // cause the container to slightly overflow the visible content area).
    // Applying the gap-equivalent padding inside `renderItem` keeps the
    // visual spacing identical while leaving `VirtualList` in sole control
    // of the scroll viewport's content height.
    const renderItem = useMemo(
        () => (msg: MockedChatMessageEntity, index: number) => (
            <div
                style={{
                    padding: `${responsive.spacing['3xl']}px ${responsive.spacing.base}px`,
                    borderRadius: responsive.borderRadius.base,
                    backgroundColor: theme.surface,
                    color: theme.text.primary,
                    fontSize: responsive.font.base,
                    // Width-constrained bubble. `VirtualList`'s list-mode row
                    // wrapper is a plain block element, so the bubble already
                    // hugs the left edge by default — no `alignSelf` needed.
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                    // First/last bubble carry the vertical breathing room
                    // that used to live on the scroll region.
                    marginTop: index === 0 ? responsive.spacing.base : undefined,
                    marginBottom: index === messages.length - 1 ? responsive.spacing.base : undefined,
                }}
            >
                {msg.text}
            </div>
        ),
        [
            responsive.spacing.xs,
            responsive.spacing.base,
            responsive.borderRadius.base,
            responsive.font.base,
            theme.surface,
            theme.text.primary,
            messages.length,
        ],
    );

    return (
        <div
            style={{
                position: 'relative',
                height: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* App bar — back button + centered title (mirrors ContactPage).
                `ExpauseAppBar` sets its own `flexShrink: 0`, so this
                `height: 100vh` flex-column shell needs no extra wrapper. */}
            <ExpauseAppBar
                rootQa={{ id: 'mock-chat-app-bar' }}
                title={l10n.mockChat.title}
                onLeadingTap={handleBack}
            />

            {/* Scrollable list region — owns the scroll, hosts VirtualList.
                Intentionally NO `paddingTop` / `paddingBottom` here: the
                `VirtualList(reversed)` bottom-pin math reads this element's
                `clientHeight` and computes the available content area by
                stripping vertical padding, so any value we set here would
                couple this caller to the bottom-pin implementation detail.
                The first/last bubble carry the equivalent margin in
                `renderItem` instead. Horizontal padding is fine. */}
            <div
                ref={scrollContainerRef}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingLeft: responsive.spacing.lg,
                    paddingRight: responsive.spacing.lg,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <VirtualList<MockedChatMessageEntity>
                    ref={virtualListRef}
                    items={messages}
                    renderItem={renderItem}
                    getItemKey={getItemKey}
                    reversed
                    scrollContainer={scrollContainerRef}
                    gap={responsive.spacing.sm}
                    defaultItemHeight={MOCK_CHAT_DEFAULT_ITEM_HEIGHT_PX}
                    // Pass the callback by reference (not as an inline arrow)
                    // so its identity stays stable across renders. `VirtualList`
                    // keeps `onReachedEnd` in its IntersectionObserver effect
                    // deps; a fresh function per render would tear down and
                    // re-create the observer on every render and re-trigger the
                    // initial intersection check each time. `prependOlderMessages`
                    // is a `useCallback` in `useMockChat`, and its
                    // `(count?: number) => void` signature is assignable to the
                    // `() => void` prop — the default `count = OLDER_BATCH_SIZE`
                    // applies when called with no args.
                    onReachedEnd={prependOlderMessages}
                />
            </div>

            {/* Composer pinned at the bottom. `MessageInput` already owns its
                own top border + theme padding, so it sits flush against the
                scroll region. */}
            <div style={{ flexShrink: 0, backgroundColor: theme.modalBackground }}>
                <MessageInput
                    ref={messageInputRef}
                    onSend={handleSend}
                    disabled={false}
                    loading={false}
                    placeholder={l10n.mockChat.placeholder}
                    qa={{ id: 'mock-chat-input' }}
                    sendQa={{ id: 'mock-chat-send' }}
                />
            </div>
        </div>
    );
};
